import { cn } from '@noor/lib/cn';
import {
  flexRender,
  type ColumnDef,
  type RowData,
  type Table as TanstackTable,
} from '@tanstack/react-table';
import type { ComponentProps, ReactNode, Ref } from 'react';
import { Skeleton } from './skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';

declare module '@tanstack/react-table' {
  // TanStack requires these parameters to match its extensible metadata interface.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    headerProps?: ComponentProps<'th'>;
    cellProps?: ComponentProps<'td'>;
    disableRowClick?: boolean;
  }
}

export interface DataTableProps<TData> {
  table: TanstackTable<TData>;
  /** @deprecated Visible columns are derived from the table instance. */
  columns?: ColumnDef<TData, unknown>[];
  isLoading?: boolean;
  emptyMessage?: ReactNode;
  /** Error content takes precedence over rows, but not initial loading. */
  errorContent?: ReactNode;
  className?: string;
  ref?: Ref<HTMLDivElement>;
  scrollRef?: Ref<HTMLDivElement>;
  containerClassName?: string;
  /** Honor column sizes, keeping header and body in a single semantic table. */
  fixedLayout?: boolean;
  bodyClassName?: string;
  cellClassName?: string;
  feedbackClassName?: string;
  skeletonRowCount?: number;
  onRowClick?: (row: TData) => void;
  rowClickDisabledColumns?: ReadonlySet<string>;
  /** Per-row classes (e.g. a status tint) — presentation only. */
  getRowClassName?: (row: TData) => string | undefined;
}

const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, [role="button"], [role="checkbox"], [role="switch"], [role="link"], [contenteditable]:not([contenteditable="false"]), [data-no-row-click]';

/**
 * Rendering only. The caller owns fetching, row identity and table state.
 *
 * Row metrics follow Figma 987:9268: 40px header/body rows, 16px gutters.
 * Column widths, sort chevrons (`TableSortIcon`) and the trailing 50px action
 * column with its 32px `more-2-fill` button live in the caller's column defs
 * (`fixedLayout` + `column.getSize()`); the first (unsorted) column renders
 * its label without an icon.
 *
 * Gutters compress to 12px below the 2xl breakpoint so fixed-width tables
 * fit laptop viewports (13" MacBook Air, 15.6" Full HD with the collapsed
 * rail) without a horizontal scrollbar; 16px gutters return on wider
 * screens. Per-column `meta.headerProps`/`meta.cellProps` classes still win
 * when they set their own padding.
 */
export function DataTable<TData>({
  table,
  isLoading = false,
  emptyMessage = 'No results.',
  errorContent,
  className,
  ref,
  scrollRef,
  containerClassName,
  fixedLayout = false,
  bodyClassName,
  cellClassName,
  feedbackClassName,
  skeletonRowCount = table.getState().pagination.pageSize,
  onRowClick,
  rowClickDisabledColumns,
  getRowClassName,
}: DataTableProps<TData>) {
  const rows = table.getRowModel().rows;
  const columns = table.getVisibleLeafColumns();
  const columnCount = Math.max(columns.length, 1);
  const placeholderCount = Number.isFinite(skeletonRowCount)
    ? Math.max(1, Math.min(Math.floor(skeletonRowCount), 10))
    : 10;

  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col overflow-hidden rounded-[20px] border border-table-border bg-card',
        className,
      )}
    >
      <Table
        containerRef={scrollRef}
        containerClassName={cn(
          'min-h-0 flex-1 overscroll-x-auto overscroll-y-contain',
          containerClassName,
        )}
        className={cn('h-full', fixedLayout && 'table-fixed border-collapse text-left')}
        style={fixedLayout ? { minWidth: table.getTotalSize() } : undefined}
        aria-busy={isLoading}
      >
        {fixedLayout && (
          <colgroup>
            {columns.map((column) => (
              <col key={column.id} style={{ width: column.getSize() }} />
            ))}
          </colgroup>
        )}
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  scope={header.colSpan > 1 ? 'colgroup' : 'col'}
                  {...header.column.columnDef.meta?.headerProps}
                  colSpan={header.colSpan}
                  className={cn(
                    // Cell gutter resolves through `--table-cell-*` sizing handles.
                    'px-[var(--table-cell-padding-x)] 2xl:px-[var(--table-cell-padding-x-2xl)]',
                    header.column.columnDef.meta?.headerProps?.className,
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className={bodyClassName}>
          {isLoading ? (
            Array.from({ length: placeholderCount }, (_, index) => (
              <TableRow key={index} className="hover:bg-transparent">
                {Array.from({ length: columnCount }, (_, cellIndex) => (
                  <TableCell key={cellIndex} className={cellClassName}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : errorContent != null || rows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={columnCount}
                className={cn('h-24 text-center', feedbackClassName)}
              >
                {errorContent ?? emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() ? 'selected' : undefined}
                className={cn(
                  onRowClick && 'cursor-pointer',
                  getRowClassName?.(row.original),
                )}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (
                          event.target !== event.currentTarget ||
                          !['Enter', ' '].includes(event.key)
                        )
                          return;
                        event.preventDefault();
                        onRowClick(row.original);
                      }
                    : undefined
                }
                onClick={
                  onRowClick
                    ? (event) => {
                        if (event.defaultPrevented || !(event.target instanceof Element))
                          return;
                        // Portalled menus bubble through React, but are outside the row.
                        if (!event.currentTarget.contains(event.target)) return;
                        if (event.target.closest(INTERACTIVE_SELECTOR)) return;
                        const cell = event.target.closest('td');
                        const columnId = cell?.getAttribute('data-col-id');
                        if (
                          cell?.hasAttribute('data-no-row-click') ||
                          (columnId && rowClickDisabledColumns?.has(columnId))
                        )
                          return;
                        onRowClick(row.original);
                      }
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    {...cell.column.columnDef.meta?.cellProps}
                    className={cn(
                      'px-[var(--table-cell-padding-x)] 2xl:px-[var(--table-cell-padding-x-2xl)]',
                      cellClassName,
                      cell.column.columnDef.meta?.cellProps?.className,
                    )}
                    data-col-id={cell.column.id}
                    data-no-row-click={
                      cell.column.columnDef.meta?.disableRowClick ? '' : undefined
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
          {/* Absorbs the height the rows leave over, so pinned-column dividers
              run to the bottom of the table whatever the row count. */}
          <TableRow
            aria-hidden
            data-slot="table-filler"
            className="h-full border-0 hover:bg-transparent"
          >
            {columns.map((column) => (
              <TableCell
                key={column.id}
                className={cn(
                  'p-0',
                  column.columnDef.meta?.cellProps?.className,
                  'h-full min-h-0',
                )}
              />
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
